import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { Loader } from '@googlemaps/js-api-loader';
import { v4 as uuid } from 'uuid';
import { environment } from '../../../environments/environment';
import { GoogleAnalyticsService } from './google-analytics.service';

export interface ILatLng {
  lat: number;
  lng: number;
}

export interface IGeocodedAddressResponse extends ILatLng {
  formattedAddress: string;
}

export interface IMarkerClickEvent extends MouseEvent {
  marker: Marker;
}

export interface IMapClickEvent extends MouseEvent, ILatLng {}

export interface IMarkerGlyph {
  backgroundColor?: string;
  borderColor?: string;
  glyphColor?: string;
  scale?: number;
}

export interface IMarkerImgGlyph extends IMarkerGlyph {
  iconUri: string;
}

export interface IMarkerElementGlyph extends IMarkerGlyph {
  element: string | Element | URL;
}

export interface INewMarkerParams {
  zone: NgZone;
  id: string;
  map: Map;
  position: ILatLng;
  title?: string;
  glyph?: Node | IMarkerImgGlyph | IMarkerElementGlyph;
}

export interface IRenderMarkerParams
  extends Omit<INewMarkerParams, 'zone' | 'map'> {}

export class Marker {
  click = new EventEmitter<IMarkerClickEvent>();

  public readonly id: string;

  private _map: Map;
  public get map(): Map {
    return this._map;
  }

  private _marker: google.maps.marker.AdvancedMarkerElement;
  public get marker(): google.maps.marker.AdvancedMarkerElement {
    return this._marker;
  }

  public get position(): ILatLng {
    const pos = this._marker.position!;
    return {
      lat: typeof pos.lat === 'number' ? pos.lat : pos.lat(),
      lng: typeof pos.lng === 'number' ? pos.lng : pos.lng(),
    };
  }
  public set position(value: ILatLng) {
    this._marker.position = value;
  }

  public get title(): string {
    return this._marker.title;
  }
  public set title(value: string) {
    this._marker.title = value;
  }

  private _zone: NgZone;

  constructor(options: INewMarkerParams) {
    this._zone = options.zone;
    this.id = options.id;

    this._map = options.map;

    let content: Node = undefined;

    if (!!options.glyph) {
      if (options.glyph instanceof Node) {
        content = options.glyph;
      } else {
        content = this.generatePinElement(options.glyph).element;
      }
    }

    this._marker = new google.maps.marker.AdvancedMarkerElement({
      position: options.position,
      map: options.map.map,
      title: options.title,
      content,
    });

    const self = this;
    this._marker.addListener('click', (event: google.maps.IconMouseEvent) => {
      event.stop();

      const clickEvent: IMarkerClickEvent = event.domEvent as IMarkerClickEvent;
      clickEvent.marker = self;

      self._zone.run(() => {
        self.click.emit(clickEvent);

        self._map.markerClick.emit(clickEvent);
      });
    });
  }

  public remove(): void {
    this._marker.map = undefined;
    this._marker.remove();
    delete this._map.markers[this.id];
  }

  public updateGlyph(glyph: IMarkerImgGlyph | IMarkerElementGlyph): void {
    this._marker.content = this.generatePinElement(glyph).element;
  }

  private generatePinElement(
    glyph: IMarkerImgGlyph | IMarkerElementGlyph
  ): google.maps.marker.PinElement {
    let glyphContent: string | Element | URL = undefined;

    if ('iconUri' in glyph) {
      glyphContent = document.createElement('img');
      (glyphContent as HTMLImageElement).src = glyph.iconUri;
    } else {
      glyphContent = glyph.element;
    }

    return new google.maps.marker.PinElement({
      glyph: glyphContent,
      background: glyph.backgroundColor,
      borderColor: glyph.borderColor,
      glyphColor: glyph.glyphColor,
      scale: glyph.scale,
    });
  }
}

export interface INewMapParams {
  element: HTMLDivElement;
  zone: NgZone;
  center?: ILatLng;
  zoom?: number;
  streetViewControl?: boolean;
  fullscreenControl?: boolean;
  mapTypeControl?: boolean;
  zoomControl?: boolean;
}

export interface IRenderMapParams extends Omit<INewMapParams, 'zone'> {}

export class Map {
  click = new EventEmitter<IMapClickEvent>();
  markerClick = new EventEmitter<IMarkerClickEvent>();

  private _map: google.maps.Map;
  public get map(): google.maps.Map {
    return this._map;
  }

  private _markers: Record<string, Marker> = {};
  public get markers(): Record<string, Marker> {
    return this._markers;
  }

  private _initialCenter: ILatLng = { lat: 44.5, lng: -89.5 };
  private _initialZoom: number = 6;

  private _zone!: NgZone;

  constructor(options: INewMapParams) {
    this._initialCenter = options.center ?? this._initialCenter;
    this._initialZoom = options.zoom ?? this._initialZoom;
    this._zone = options.zone;

    this._map = new google.maps.Map(options.element, {
      center: options.center,
      zoom: options.zoom,
      mapId: `map-${uuid()}`,
      streetViewControl: options.streetViewControl,
      fullscreenControl: options.fullscreenControl,
      mapTypeControl: options.mapTypeControl,
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      zoomControl: options.zoomControl,
    });

    const self = this;
    this._map.addListener('click', (event: google.maps.MapMouseEvent) => {
      event.stop();

      const clickEvent: IMapClickEvent = event.domEvent as IMapClickEvent;
      clickEvent.lat = event.latLng.lat();
      clickEvent.lng = event.latLng.lng();
      self._zone.run(() => {
        self.click.emit(clickEvent);
      });
    });
  }

  public addMarker(options: IRenderMarkerParams): Marker {
    const newMarker = new Marker({
      zone: this._zone,
      id: options.id,
      map: this,
      position: options.position,
      title: options.title,
      glyph: options.glyph,
    });

    this._markers[options.id] = newMarker;

    return newMarker;
  }

  public recenter(position: ILatLng, zoom?: number): void {
    this._map.setCenter(position);

    if (zoom) {
      this._map.setZoom(zoom);
    }
  }

  public resetView(): void {
    this._map.setCenter(this._initialCenter);
    this._map.setZoom(this._initialZoom);
  }

  public addControl(
    control: HTMLElement,
    position: google.maps.ControlPosition
  ) {
    this._map.controls[position].push(control);
  }
}

@Injectable({
  providedIn: 'root',
})
export class MapsService {
  private _libraryLoaded = false;
  public get libraryLoaded(): boolean {
    return this._libraryLoaded;
  }

  private geocoder: google.maps.Geocoder;
  private elevator: google.maps.ElevationService;

  constructor(private zone: NgZone, private analytics: GoogleAnalyticsService) {
    this.init();
  }

  public geocodeAddress(
    address: string
  ): Promise<IGeocodedAddressResponse | void> {
    return new Promise((resolve, reject) => {
      if (!this._libraryLoaded) {
        setTimeout(() => {
          this.geocodeAddress(address).then(resolve, reject);
        }, 250);

        return;
      }

      this.analytics.trackEvent('Google Maps', {
        action: 'Location Geocoded',
      });

      this.geocoder
        .geocode({ address })
        .then((response) => {
          if (!response.results.length) {
            console.warn(
              '[MapsService] geocodeAddress(%s) no results',
              address
            );
            resolve();
            return;
          }

          resolve({
            lat: response.results[0].geometry.location.lat(),
            lng: response.results[0].geometry.location.lng(),
            formattedAddress: response.results[0].formatted_address,
          });
        })
        .catch((reason: google.maps.MapsRequestError) => {
          if (reason.code === google.maps.GeocoderStatus.ZERO_RESULTS) {
            console.warn(
              '[MapsService] geocodeAddress(%s) no results',
              address
            );
            resolve();
            return;
          }

          console.error(
            '[MapsService] geocodeAddress(%s) error',
            address,
            reason
          );
          reject(reason);
        });
    });
  }

  public geocodeLatLng(
    latLng: ILatLng
  ): Promise<IGeocodedAddressResponse | void> {
    return new Promise((resolve, reject) => {
      if (!this._libraryLoaded) {
        setTimeout(() => {
          this.geocodeLatLng(latLng).then(resolve, reject);
        }, 250);

        return;
      }

      this.analytics.trackEvent('Google Maps', {
        action: 'Location Geocoded',
      });

      this.geocoder
        .geocode({ location: latLng })
        .then((response) => {
          if (!response.results.length) {
            console.warn('[MapsService] geocodeLatLng(%o) no results', latLng);
            resolve();
            return;
          }

          resolve({
            lat: response.results[0].geometry.location.lat(),
            lng: response.results[0].geometry.location.lng(),
            formattedAddress: response.results[0].formatted_address,
          });
        })
        .catch((reason: google.maps.MapsRequestError) => {
          if (reason.code === google.maps.GeocoderStatus.ZERO_RESULTS) {
            console.warn('[MapsService] geocodeLatLng(%o) no results', latLng);
            resolve();
            return;
          }

          console.error(
            '[MapsService] geocodeLatLng(%o) error',
            latLng,
            reason
          );
          reject(reason);
        });
    });
  }

  public getElevation(latLng: ILatLng): Promise<number | undefined> {
    return new Promise((resolve, reject) => {
      if (!this._libraryLoaded) {
        setTimeout(() => {
          this.getElevation(latLng).then(resolve, reject);
        }, 250);

        return;
      }

      this.analytics.trackEvent('Google Maps', {
        action: 'Elevation Requested',
      });

      this.elevator
        .getElevationForLocations({
          locations: [latLng],
        })
        .then((response) => {
          if (!response.results.length) {
            console.warn('[MapsService] getElevation(%o) no results', latLng);
            resolve(undefined);
            return;
          }

          resolve(Math.round(response.results[0].elevation * 3.28084));
        })
        .catch(reject);
    });
  }

  public renderMap(options: IRenderMapParams): Promise<Map> {
    return new Promise((resolve, reject) => {
      if (!this._libraryLoaded) {
        setTimeout(() => {
          this.renderMap(options).then(resolve, reject);
        }, 250);

        return;
      }

      this.analytics.trackEvent('Google Maps', {
        action: 'Map Rendered',
      });

      const newMapOptions: INewMapParams = {
        ...options,
        zone: this.zone,
      };

      resolve(new Map(newMapOptions));
    });
  }

  private init() {
    const loader = new Loader({
      apiKey: environment.googleApi.maps.key,
      libraries: ['maps', 'geocoding', 'marker', 'elevation'],
    });

    loader.importLibrary('core').then(() => {
      this.geocoder = new google.maps.Geocoder();
      this.elevator = new google.maps.ElevationService();
      this._libraryLoaded = true;
    });
  }
}
